import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, useTheme } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import {
  AREA_SERVICED_STEP_KM,
  MAX_AREA_SERVICED_KM,
  MIN_AREA_SERVICED_KM,
} from "./constants";
import { clampAreaServiced } from "./helpers";

interface AreaServicedSelectorProps {
  value: number;
  onChange: (radiusKm: number) => void;
}

const AreaServicedSelector: React.FC<AreaServicedSelectorProps> = ({
  value,
  onChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("services");

  const handleStep = useCallback(
    (stepKm: number) => onChange(clampAreaServiced(value + stepKm)),
    [onChange, value],
  );

  // Ignore empty/invalid input so the vendor can clear the box while typing;
  // the last valid radius stays selected until they enter a new one.
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enteredKm = Number(event.target.value);
      if (event.target.value === "" || Number.isNaN(enteredKm)) return;

      onChange(clampAreaServiced(enteredKm));
    },
    [onChange],
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
        }}
      >
        <IconButton
          aria-label={t("wizard.setup.decrease")}
          onClick={() => handleStep(-AREA_SERVICED_STEP_KM)}
          disabled={value <= MIN_AREA_SERVICED_KM}
        >
          <Remove />
        </IconButton>

        <TextField
          name="areaServiced"
          type="number"
          value={value}
          step={AREA_SERVICED_STEP_KM}
          onChange={handleInputChange}
          textAlign="center"
          endAdornment={<Typography variant="body1">km</Typography>}
          style={{ flex: 1 }}
        />

        <IconButton
          aria-label={t("wizard.setup.increase")}
          onClick={() => handleStep(AREA_SERVICED_STEP_KM)}
          disabled={value >= MAX_AREA_SERVICED_KM}
        >
          <Add />
        </IconButton>
      </div>

      <Typography variant="caption" align="center">
        {t("wizard.setup.radius", {
          min: MIN_AREA_SERVICED_KM,
          max: MAX_AREA_SERVICED_KM,
        })}
      </Typography>
    </div>
  );
};

export default AreaServicedSelector;
